import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostTarget } from 'types'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import Styles from './ScheduledPostTargetsPreview.module.scss'
import cx from 'classnames'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { ProviderToName as BaseProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { Text } from 'blaze-ui/Text/Text'
import { InstagramBrand } from 'blaze-ui/icons/24'
import { FacebookBrand } from 'blaze-ui/icons/35'
import { TwitterBrand, Lock3, LinkAngled, Send1, EyeOpen, ArrowRotateLeft2 } from 'blaze-ui/icons/20'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { TikTokBrand, YouTubeBrand, AudioSettings } from 'blaze-ui/icons'
import ConnectButton from './ConnectButton/ConnectButton'
import { Spinner } from 'common/Spinner/Spinner'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import RetryButton from './RetryButton/RetryButton'
import ViewPostButton from './ViewPostButton/ViewPostButton'
import PostNowButton from './PostNowButton/PostNowButton'
import { usePublishScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/usePublishScheduledPostTargets'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { getTargetActionConfig } from './utils/getTargetActionConfig'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { PostSettingsModal } from 'components/PostSettingsModal'
import { useModals } from 'almanac-ui/Modal'
import { getChannelForPlatform } from 'screens/Calendar/FullScreenPreview/utils'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { Pressable } from '@react-aria/interactions'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { MobileActionButton } from './MobileActionButton/MobileActionButton'
import { noop } from 'lodash'
import { MobileScheduledPostTargetPreviewItem } from './MobileScheduledPostTargetPreviewItem/MobileScheduledPostTargetPreviewItem'
import { IconWithStatus } from './IconWithStatus/IconWithStatus'

const ProviderToName = {
  ...BaseProviderToName,
  [ExternalIntegrationEnum.X]: 'X / Twitter',
}

export interface ValidationResult {
  isValid: boolean
  errors: string[]
}

export const providerIcon: Partial<Record<ExternalIntegrationEnum, JSX.Element>> = {
  instagram: <InstagramBrand />,
  linkedin: <LinkedInBrand size={24} />,
  x: <TwitterBrand size={24} />,
  facebook: <FacebookBrand size={24} />,
  tiktok: <TikTokBrand size={24} />,
  youtube: <YouTubeBrand size={24} />,
}

interface ScheduledPostTargetPreviewItemProps {
  platform: ExternalIntegrationEnum
  target?: ScheduledPostTarget
  isAvailable: boolean
  onToggle: (platform: ExternalIntegrationEnum, isChecked: boolean) => void
  onCreateAndPublish?: (platform: ExternalIntegrationEnum) => void
  isActive?: boolean
  onPress: () => void
  isDisabled?: boolean
  validation: ValidationResult
  isLoading?: boolean
  isAlreadyPosted?: boolean
  needsReauth?: boolean
  isLocked?: boolean
}

export const ScheduledPostTargetPreviewItem: React.FC<ScheduledPostTargetPreviewItemProps> = ({
  platform,
  target,
  isAvailable,
  onToggle,
  onCreateAndPublish,
  isActive = false,
  onPress,
  isDisabled = false,
  validation,
  isLoading = false,
  isAlreadyPosted = false,
  needsReauth = false,
  isLocked = false,
}) => {
  const { connect } = useConnectAccountPopup()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const openWindowSafely = useOpenWindowSafely()
  const { openModal } = useModals()
  const approvalsEnabled = useFlipperFlag('post_approvals')
  const { isMobile } = useResponsiveDesign()

  const onConnect = (popUp?: Window) => {
    connect(popUp, {
      onConnect: () => {
        queryClient.invalidateQueries(calendarKeys.all)
        queryClient.invalidateQueries(['externalIntegrations', workspaceId])
      },
    })
  }

  const hasTarget = !!target
  const { mutate: publishScheduledPostTargets } = usePublishScheduledPostTargets()
  const { data: externalIntegrations } = useExternalIntegrations()
  const existingIntegration = externalIntegrations?.external_integrations.find(
    (integration) => integration.provider === platform,
  )

  const onReauth = async (platform: ExternalIntegrationEnum) => {
    const accountToRefreshId = existingIntegration?.id

    if (accountToRefreshId) {
      localStorage.setItem(`${platform}_accountToRefreshId`, accountToRefreshId.toString())
    }
  }

  const actionConfig = getTargetActionConfig({
    platform,
    target,
    isAvailable,
    validation,
    isLoading,
    isAlreadyPosted,
    isDisabled,
    hasTarget,
    needsReauth,
    onConnect: () => {
      // This will be used as a callback for InitiateOauthTrigger
    },
    onToggle,
    onPublish: (targetId) =>
      target?.scheduled_post_id &&
      publishScheduledPostTargets({ scheduledPostId: target.scheduled_post_id, targetIds: [targetId] }),
    onPublishWithoutTarget: () => {
      // Create the target and immediately publish it
      onCreateAndPublish?.(platform) || onToggle(platform, true)
    },
    onViewPost: (url) => openWindowSafely(url),
    onRetry: (targetId) =>
      target?.scheduled_post_id &&
      publishScheduledPostTargets({ scheduledPostId: target.scheduled_post_id, targetIds: [targetId] }),
    onReauth,
    isLocked,
  })

  const getActionElement = () => {
    switch (actionConfig.type) {
      case 'loading':
        return (
          <div>
            <Spinner />
          </div>
        )

      case 'posting':
        return <span className={Styles.label}>Posting</span>

      case 'locked':
        if (isMobile) {
          return (
            <div className={Styles.toggleButton}>
              <IconButton variant="ghost" color="var(--dark-25)" isDisabled size={'sm'} icon={Lock3} />
              <ToggleButton
                name={`${platform}-locked-toggle`}
                checked={false}
                disabled={true}
                handleChange={noop}
                size={'sm-md'}
                isAPMobileWeb
              />
            </div>
          )
        }
        return (
          <IconButton
            variant="ghost"
            color="var(--dark-80)"
            isDisabled
            size={approvalsEnabled ? 'xs' : 'sm'}
            icon={Lock3}
          />
        )

      case 'connect':
        if (isDisabled) {
          return <></>
        }
        if (isMobile) {
          return (
            <InitiateOauthTrigger popUp provider={platform} onInitiatedOauth={onConnect}>
              <MobileActionButton onPress={actionConfig.onPress} label="Connect" icon={LinkAngled} />
            </InitiateOauthTrigger>
          )
        }
        if (approvalsEnabled) {
          return <span className={Styles.label}>Connect</span>
        }
        return (
          <InitiateOauthTrigger popUp provider={platform} onInitiatedOauth={onConnect}>
            <ConnectButton />
          </InitiateOauthTrigger>
        )

      case 'post-now':
        if (isMobile) {
          return <MobileActionButton onPress={actionConfig.onPress} label="Post Now" icon={Send1} />
        }
        if (approvalsEnabled) {
          return <span className={Styles.label}>Post Now</span>
        }
        return <PostNowButton onPress={actionConfig.onPress} disabled={actionConfig.disabled} />

      case 'view-post':
        if (isMobile) {
          return <MobileActionButton onPress={actionConfig.onPress} label="View Post" icon={EyeOpen} />
        }
        if (approvalsEnabled) {
          return <span className={Styles.label}>View</span>
        }
        return <ViewPostButton onPress={actionConfig.onPress} />

      case 'retry-auth':
        if (isMobile) {
          return (
            <InitiateOauthTrigger
              popUp
              provider={platform}
              onInitiatedOauth={onConnect}
              accountToRefreshId={existingIntegration?.id}
            >
              <MobileActionButton onPress={actionConfig.onPress} label="Retry" icon={ArrowRotateLeft2} />
            </InitiateOauthTrigger>
          )
        }
        if (approvalsEnabled) {
          return <span className={cx(Styles.label, Styles.errorLabel)}>Retry</span>
        }
        return (
          <InitiateOauthTrigger
            popUp
            provider={platform}
            onInitiatedOauth={onConnect}
            accountToRefreshId={existingIntegration?.id}
          >
            <RetryButton onPress={actionConfig.onPress} />
          </InitiateOauthTrigger>
        )

      case 'retry':
        if (isMobile) {
          return <MobileActionButton onPress={actionConfig.onPress} label="Retry" icon={ArrowRotateLeft2} />
        }
        if (approvalsEnabled) {
          return <span className={cx(Styles.label, Styles.errorLabel)}>Retry</span>
        }
        return <RetryButton onPress={actionConfig.onPress} />

      case 'toggle':
      default:
        const desktopIconSize = approvalsEnabled ? 'xs' : 'sm-md'
        const iconSize = isMobile ? 'sm-md' : desktopIconSize

        return (
          <div className={Styles.toggleButton}>
            <ToggleButton
              name={`${platform}-toggle`}
              checked={hasTarget}
              disabled={actionConfig.disabled}
              handleChange={(e) => {
                e.stopPropagation()
                actionConfig.onPress?.()
              }}
              size={iconSize}
              isAPMobileWeb={isMobile}
            />
          </div>
        )
    }
  }

  const handlePostSettingsClick = () => {
    if (!target?.scheduled_post_id || !target?.id) return

    const channel = getChannelForPlatform(platform)

    if (!channel) return
    openModal(PostSettingsModal, {
      channel,
      scheduledPostId: target.scheduled_post_id,
      scheduledPostTargetId: target.id,
    })
  }

  const getButtonTooltipLabel = () => {
    return actionConfig.buttonTooltip || ''
  }

  const shouldShowButtonTooltip = () => {
    return !!actionConfig.buttonTooltip
  }

  const getComponentTooltipLabel = () => {
    return actionConfig.componentTooltip || ''
  }

  const shouldShowComponentTooltip = () => {
    return !!actionConfig.componentTooltip
  }

  if (isMobile) {
    return (
      <MobileScheduledPostTargetPreviewItem
        getActionElement={getActionElement}
        needsReauth={needsReauth}
        platform={platform}
        providerIcon={providerIcon[platform]}
        target={target}
        validation={validation}
      />
    )
  }

  return (
    <ConditionalWrapper
      condition={approvalsEnabled && ['connect', 'retry-auth'].includes(actionConfig.type)}
      wrapper={(children) => (
        <InitiateOauthTrigger
          popUp
          provider={platform}
          onInitiatedOauth={onConnect}
          accountToRefreshId={actionConfig.type === 'retry-auth' ? existingIntegration?.id : undefined}
        >
          <Pressable>{children}</Pressable>
        </InitiateOauthTrigger>
      )}
    >
      <div
        data-testid={`${platform}-platform`}
        className={cx(Styles.platformItem, { [Styles.active]: isActive && !approvalsEnabled })}
        onClick={(e) => {
          if (approvalsEnabled) {
            actionConfig.onPress?.()
          } else {
            onPress()
          }
        }}
      >
        <Tooltip label={getComponentTooltipLabel()} hide={!shouldShowComponentTooltip()}>
          <div className={Styles.iconWithPlatformName} data-testid={`${platform}-icon-with-platform-name`}>
            <IconWithStatus
              providerIcon={providerIcon[platform]}
              target={target}
              validation={validation}
              needsReauth={needsReauth}
            />
            {!approvalsEnabled && (
              <Text variant="smallList" color="var(--dark-80)">
                {ProviderToName[platform]}
              </Text>
            )}
          </div>
        </Tooltip>
        <div className={Styles.postSettingsButtonGroup}>
          {actionConfig.showPostSettings && (
            <IconButton
              onPress={handlePostSettingsClick}
              variant="ghost"
              className={Styles.postSettingsButton}
              color="var(--dark-80)"
              icon={AudioSettings}
              data-testid="post-settings-button"
              isDisabled={isLoading || !hasTarget}
            />
          )}
          <Tooltip label={getButtonTooltipLabel()} hide={!shouldShowButtonTooltip()}>
            <div>{getActionElement()}</div>
          </Tooltip>
        </div>
      </div>
    </ConditionalWrapper>
  )
}
